import React from 'react';
import { Box, Chip, Typography, Divider } from '@mui/material';
import { StarRate, CorporateFare, Tour, Home, Public, SyncAlt } from '@mui/icons-material';
import ExtensionIcon from '@mui/icons-material/Extension';
import Circle from '@mui/icons-material/Circle';
import DrawerMenu from '../DrawerMenu';

const FLAG_CHIP_DEFAULT = { bg: '#F2F4F7', color: '#344054' };
const FLAG_CHIP_COLORS: Record<string, { bg: string; color: string }> = {
  ids: { bg: '#E3F2FD', color: '#0D47A1' },
  customer: { bg: '#FFF4D6', color: '#B8860B' },
  beo: { bg: '#E8F5E9', color: '#2E7D32' },
};

interface LegendInfoProps {
  open: boolean;
  handleClose: () => void;
  calendarFlag: any;
  strings: any;
}

const AVAILABILITY_STATUSES = ['HIGH_DEMAND', 'UNAVAILABLE', 'BLOCKED'];

const StatusDot = ({ item }: { item: any }) => (
  <Circle
    sx={{
      mr: 0.75,
      fontSize: 12,
      width: 12,
      height: 12,
      minWidth: 12,
      ...(item.patternBackground
        ? {
            background: item.patternBackground,
            color: 'transparent',
            border: `1px solid ${item.borderColor ?? item.color}`,
            borderRadius: '50%',
            padding: 0,
          }
        : {
            color: item.color,
          }),
    }}
  />
);

const StatusGrid = ({ items }: { items: any[] }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: 1,
      rowGap: 0.75,
    }}>
    {items.map((item: any) => (
      <Box display="flex" alignItems="center" minWidth={0} key={item.class || item.status}>
        <StatusDot item={item} />
        <Typography fontSize="13px" fontWeight="500" sx={{ lineHeight: 1.25 }}>
          {item.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const LegendInfo: React.FC<LegendInfoProps> = ({ open, handleClose, calendarFlag, strings }) => {
  const bookingIcons = [
    { icon: StarRate, label: strings.calendar_tooltip_flag_customer, colorKey: 'customer' },
    { icon: CorporateFare, label: strings.calendar_legend_flag_corporate },
    { icon: Tour, label: strings.calendar_tooltip_flag_beo, colorKey: 'beo' },
    { icon: Home, label: strings.calendar_tooltip_flag_rooms },
    { icon: Public, label: strings.calendar_tooltip_flag_external, colorKey: 'ids' },
    { icon: SyncAlt, label: strings.calendar_tooltip_flag_ids, colorKey: 'ids' },
    { icon: ExtensionIcon, label: strings.calendar_tooltip_flag_programming },
  ];

  const statusRows = calendarFlag(strings);
  const bookingStatuses = statusRows.filter((item: any) => !AVAILABILITY_STATUSES.includes(item.status));
  const availabilityStatuses = statusRows.filter((item: any) => AVAILABILITY_STATUSES.includes(item.status));

  return (
    <DrawerMenu
      open={open}
      handleClose={handleClose}
      closeButton
      flexColumn
      overlapHeader
      width={350}
      title="Calendar Legend">
      <Box sx={{ width: '100%' }}>
        <Typography fontSize="13px" fontWeight="700" mb={1} sx={{ letterSpacing: 0.2, textTransform: 'uppercase' }}>
          {strings.calendar_legend_booking_status}
        </Typography>
        <StatusGrid items={bookingStatuses} />

        <Typography
          fontSize="13px"
          fontWeight="700"
          mb={1}
          mt={2}
          sx={{ letterSpacing: 0.2, textTransform: 'uppercase' }}>
          {strings.calendar_legend_availability}
        </Typography>
        <StatusGrid items={availabilityStatuses} />

        <Divider sx={{ my: 2 }} />
        <Typography fontSize="13px" fontWeight="700" mb={1} sx={{ letterSpacing: 0.2, textTransform: 'uppercase' }}>
          {strings.calendar_legend_booking_types}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 1,
            rowGap: 0.75,
          }}>
          {bookingIcons.map(item => {
            const IconComponent = item.icon;
            const palette = (item.colorKey && FLAG_CHIP_COLORS[item.colorKey]) || FLAG_CHIP_DEFAULT;
            return (
              <Chip
                key={item.label}
                size="small"
                icon={<IconComponent />}
                label={item.label}
                variant="filled"
                sx={{
                  justifyContent: 'flex-start',
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: palette.bg,
                  color: palette.color,
                  '& .MuiChip-icon': { fontSize: 14, ml: 0.5, color: 'inherit' },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </DrawerMenu>
  );
};

export default LegendInfo;
